import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBdPorFuera } from "@/lib/api";
import ConversationsByTagSection from "@/pages/client-tabs/ConversationsByTagSection";
import { Input } from "@/components/ui/input";
import { Search, Mail, Linkedin, MessageCircle, ExternalLink, AlertCircle, Filter } from "lucide-react";

const FORME_CLIENT_ID = "46fffad1-0e87-42bc-8bdc-7132b8ca3502";

export default function ConversacionesOutboundSection({ clientId, skyleadAccounts, hasPorFuera }) {
  const hasSkylead = Array.isArray(skyleadAccounts) && skyleadAccounts.length > 0;
  // Show Por Fuera for For Me (default sheet) OR any client with a custom URL configured
  const showPorFuera = clientId === FORME_CLIENT_ID || hasPorFuera;
  return (
    <div className="space-y-6" data-testid="bd-conv-outbound">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Conversaciones outbound</p>
        <h3 className="font-display text-base font-semibold text-slate-900">Plataforma outreach + manual (Por Fuera)</h3>
      </div>

      {hasSkylead ? (
        <ConversationsByTagSection clientId={clientId} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md p-6 text-center">
          <Linkedin className="h-8 w-8 mx-auto mb-2 text-slate-400" />
          <h4 className="font-display text-sm font-semibold text-slate-900 mb-1">Sin perfiles outreach configurados</h4>
          <p className="text-xs text-slate-500">Configurá los perfiles en <span className="font-medium">Configurar compromiso</span> para ver las conversaciones por tag.</p>
        </div>
      )}

      {showPorFuera && <PorFueraSection clientId={clientId} />}
    </div>
  );
}

function PorFueraSection({ clientId }) {
  const q = useQuery({
    queryKey: ["bd-porfuera", clientId],
    queryFn: () => fetchBdPorFuera(clientId),
    staleTime: 60_000,
    retry: false,
  });
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("Todos");
  const [canalFilter, setCanalFilter] = useState("Todos");

  const filtered = useMemo(() => {
    const list = q.data?.leads || [];
    return list.filter((l) => {
      if (estadoFilter !== "Todos" && l.estado !== estadoFilter) return false;
      if (canalFilter !== "Todos" && l.canal !== canalFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return [l.nombre, l.empresa, l.posicion, l.info, l.referido_de, l.comentarios]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [q.data, search, estadoFilter, canalFilter]);

  if (q.isLoading) return <div className="h-72 rounded-md skeleton" />;
  if (q.error) return (
    <div className="bg-white border border-rose-200 rounded-md p-4 text-xs text-rose-700 flex items-center gap-2">
      <AlertCircle className="h-4 w-4" /> No se pudo cargar la hoja Por Fuera.
    </div>
  );

  const data = q.data || {};
  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 min-w-0 max-w-full overflow-hidden" data-testid="bd-por-fuera">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-sm font-semibold text-slate-900">Por Fuera · For Me</h3>
            <a href={data.sheet_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1">
              Abrir planilla <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{filtered.length} de {data.count} contactos · referidos, correos directos y WhatsApp</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select value={canalFilter} onChange={(e) => setCanalFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="pf-canal">
            <option value="Todos">Todos los canales</option>
            {(data.by_canal || []).map((c) => <option key={c.label} value={c.label}>{c.label} ({c.count})</option>)}
          </select>
          <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="pf-estado">
            <option value="Todos">Todos los estados</option>
            {(data.by_estado || []).map((e) => <option key={e.label} value={e.label}>{e.label} ({e.count})</option>)}
          </select>
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Buscar nombre, empresa, info…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="pf-search" />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {(data.by_estado || []).slice(0, 4).map((e) => (
          <div key={e.label} className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate" title={e.label}>{e.label}</div>
            <div className="text-lg font-semibold text-slate-900 tabular-nums">{e.count}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-5 max-h-[600px]">
        <table className="w-full text-[11px] min-w-[1100px]">
          <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[80px]">Canal</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[140px]">Tipo</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[180px]">Nombre</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[160px]">Empresa</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Posición / Info</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[130px]">Referido de</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[140px]">Estado</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-[200px]">Comentarios</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-2 align-middle"><CanalBadge canal={l.canal} /></td>
                <td className="px-2 py-2 align-middle text-slate-600 text-[10px]">{l.tipo_lead}</td>
                <td className="px-2 py-2 align-middle text-slate-900 font-medium truncate max-w-[180px]" title={l.nombre || "(sin nombre)"}>
                  {l.nombre || <span className="text-slate-400 italic">(sin nombre)</span>}
                </td>
                <td className="px-2 py-2 align-middle text-slate-700 truncate max-w-[160px]" title={l.empresa}>{l.empresa || "—"}</td>
                <td className="px-2 py-2 align-middle text-slate-600">
                  {l.posicion && <div className="text-slate-700 truncate" title={l.posicion}>{l.posicion}</div>}
                  {l.info && <div className="text-[10px] text-slate-500 truncate" title={l.info}>{l.info}</div>}
                </td>
                <td className="px-2 py-2 align-middle text-slate-500 truncate max-w-[130px]" title={l.referido_de}>{l.referido_de || "—"}</td>
                <td className="px-2 py-2 align-middle"><EstadoBadge estado={l.estado} /></td>
                <td className="px-2 py-2 align-middle text-slate-600 text-[10px] truncate max-w-[200px]" title={l.comentarios}>{l.comentarios || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-xs text-slate-500 py-6">Sin contactos para mostrar.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CanalBadge({ canal }) {
  const c = (canal || "").toLowerCase();
  if (c.includes("email")) return <span className="inline-flex items-center gap-1 text-[10px] text-violet-700"><Mail className="h-3 w-3" /> {canal}</span>;
  if (c.includes("linkedin")) return <span className="inline-flex items-center gap-1 text-[10px] text-[#0A66C2]"><Linkedin className="h-3 w-3" /> {canal}</span>;
  if (c.includes("whatsapp")) return <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700"><MessageCircle className="h-3 w-3" /> {canal}</span>;
  return <span className="text-[10px] text-slate-500">{canal || "—"}</span>;
}

function EstadoBadge({ estado }) {
  if (!estado) return <span className="text-[10px] text-slate-400">—</span>;
  const e = estado.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (e.includes("reu ok") || e.includes("acepta")) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (e.includes("pendiente")) tone = "bg-amber-50 text-amber-700 border-amber-200";
  else if (e.includes("no avanza") || e.includes("no interes")) tone = "bg-rose-50 text-rose-700 border-rose-200";
  else if (e.includes("responde")) tone = "bg-indigo-50 text-indigo-700 border-indigo-200";
  return <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-sm border ${tone} truncate max-w-[140px]`} title={estado}>{estado}</span>;
}
